/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { admin, brand } from "../../config/appData";
import { getAdminAuth, getAdminAccessModules, getAdminProfile, isAdminPathAllowed } from "../../utils/auth";
import "./AdminLayout.css";
import { clearLoginDataFunction } from "../../utils/UtilityFunctions";
import { useDispatch } from "react-redux";
import { onGetEnquiries, onEnquiryView } from "../../Store/Slices/enquirySlice";
import { onGetBusinesses } from "../../Store/Slices/businessSlice";
import { onGetUsers } from "../../Store/Slices/userSlice";
import { useEnquiryData } from "../../Utility";
  
const SIDEBAR_WIDTH_OPEN = 260;
const SIDEBAR_WIDTH_COLLAPSED = 72;

function AdminLayout({ children }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const enquiryData = useEnquiryData();
  useEffect(() => {
    dispatch(onGetEnquiries());
    dispatch(onGetBusinesses({ all: true, limit: 50, offset: 0 }));
    dispatch(onGetUsers());
  }, []);

  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [profileOpen, setProfileOpen] = useState(false);

  const allowedModules = getAdminAccessModules();
  const menuItems =
    allowedModules?.length > 0
      ? admin.menuItems.filter((item) => allowedModules.includes(item.path))
      : admin.menuItems;
  const firstAllowedPath = menuItems.length > 0 ? menuItems[0].path : "/admin/dashboard";

  useEffect(() => {
    if (!getAdminAuth()) {
      navigate("/admin/login", { replace: true });
      return;
    }
    if (allowedModules?.length > 0 && !isAdminPathAllowed(location.pathname, allowedModules)) {
      navigate(firstAllowedPath, { replace: true });
    }
  }, [navigate, location.pathname, allowedModules, firstAllowedPath]);

  const pageTitle =
    menuItems.find((item) => item.path === location.pathname)?.label ??
    admin.menuItems.find((item) => item.path === location.pathname)?.label ??
    "Admin";
  const notificationCount =
    enquiryData?.getData?.filter((enquiry) => enquiry.viewed === false)
      ?.length ?? 0;
  const notificationBadge =
    notificationCount > 0
      ? notificationCount >= 100
        ? "99+"
        : String(notificationCount)
      : null;
  const hasNotification = notificationCount > 0;
  const viewLoading = enquiryData?.viewLoading ?? false;

  const handleNotificationClick = () => {
    if (!hasNotification || viewLoading) return;
    dispatch(onEnquiryView({
      seen_by: profile?.id,
    }));
    navigate("/admin/enquiries-users");
  };

  useEffect(() => {
    if (!getAdminAuth()) {
      navigate("/admin/login", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    const closeProfile = () => setProfileOpen(false);
    if (profileOpen) {
      document.addEventListener("click", closeProfile);
      return () => document.removeEventListener("click", closeProfile);
    }
  }, [profileOpen]);

  if (!getAdminAuth()) {
    return null;
  }

  const profile = getAdminProfile();
  const profileName = profile?.name?.trim() || (admin.profileName ?? "Admin");
  const profileEmail = profile?.email?.trim() || (admin.profileEmail ?? "admin@weddingzvenue.com");
  const profileRole = profile?.role?.trim() || null;
  const profileInitials =
    (profileName && profileName.length >= 2
      ? profileName.trim().slice(0, 2).toUpperCase()
      : profileEmail && profileEmail.length >= 2
        ? profileEmail.trim().slice(0, 2).toUpperCase()
        : null) ?? admin.profileInitials ?? "A";

  const handleLogout = (e) => {
    e?.stopPropagation?.();
    clearLoginDataFunction();
    setProfileOpen(false);
    navigate("/admin/login", { replace: true });
  };

  const sidebarWidth = sidebarOpen
    ? SIDEBAR_WIDTH_OPEN
    : SIDEBAR_WIDTH_COLLAPSED;

  return (
    <div className="admin-layout">
      <div
        className={`admin-sidebar-overlay ${sidebarOpen ? "visible" : ""}`}
        onClick={() => setSidebarOpen(false)}
        aria-hidden="true"
      />

      <aside
        className={`admin-sidebar ${sidebarOpen ? "open" : "closed"}`}
        style={{ width: sidebarWidth }}
      >
        <div className="admin-sidebar-header">
          <Link to={firstAllowedPath} className="admin-sidebar-logo">
            <span className="admin-sidebar-logo-icon">{brand.logoIcon}</span>
            <span className="admin-sidebar-logo-text">{brand.name}</span>
          </Link>
        </div>
        <nav className="admin-sidebar-nav">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`admin-sidebar-link ${location.pathname === item.path ? "active" : ""}`}
              onClick={() => setSidebarOpen(false)}
              title={!sidebarOpen ? item.label : undefined}
            >
              <span className="admin-sidebar-link-icon">{item.icon}</span>
              <span className="admin-sidebar-link-label">{item.label}</span>
              {!sidebarOpen && (
                <span className="admin-sidebar-tooltip" aria-hidden="true">
                  {item.label}
                </span>
              )}
            </Link>
          ))}
         
        </nav>
        <div className="admin-sidebar-footer">
          <div className="admin-sidebar-footer-brand">
            <span className="admin-sidebar-footer-icon">{brand.logoIcon}</span>
            <span className="admin-sidebar-footer-name">{brand.name}</span>
          </div>
        </div>
      </aside>

      <div
        className={`admin-main-wrap ${!sidebarOpen ? "sidebar-collapsed" : ""}`}
        style={{ marginLeft: sidebarWidth }}
      >
        <header className="admin-header">
          <button
            type="button"
            className="admin-header-menu-btn"
            onClick={() => setSidebarOpen((v) => !v)}
            aria-label={sidebarOpen ? "Close sidebar" : "Open sidebar"}
            aria-expanded={sidebarOpen}
          >
            {sidebarOpen ? (
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            ) : (
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            )}
          </button>
          <h1 className="admin-header-title">{pageTitle}</h1>
          <div className="admin-header-actions">
            <button
              type="button"
              className={`admin-header-icon-btn admin-header-notification-btn ${!hasNotification ? "admin-header-notification-btn-disabled" : ""}`}
              onClick={handleNotificationClick}
              disabled={!hasNotification || viewLoading}
              aria-label={notificationBadge ? `Notifications (${notificationBadge})` : "Notifications"}
            >
              <svg
                className="admin-header-notification-icon"
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              </svg>
              {notificationBadge && (
                <span className="admin-header-notification-count" aria-hidden="true">
                  {notificationBadge}
                </span>
              )}
            </button>
            <div className="admin-header-profile-wrap">
              <button
                type="button"
                className="admin-header-avatar-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setProfileOpen((v) => !v);
                }}
                aria-label="Profile"
                aria-expanded={profileOpen}
                aria-haspopup="true"
              >
                <span className="admin-header-avatar admin-header-avatar-initials">
                  {profileInitials}
                </span>
              </button>
              {profileOpen && (
                <div
                  className="admin-header-profile-dropdown"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="admin-header-profile-head">
                    <span className="admin-header-profile-initials">
                      {profileInitials}
                    </span>
                    <div className="admin-header-profile-info">
                      <span className="admin-header-profile-name">
                        {profileName}
                      </span>
                      <span className="admin-header-profile-email">
                        {profileEmail}
                      </span>
                      {profileRole && (
                        <span className="admin-header-profile-role">
                          {profileRole}
                        </span>
                      )}
                    </div>
                  </div>
                  <Link
                    to="/admin/forgot-password"
                    className="admin-header-profile-item admin-header-profile-link"
                    onClick={() => setProfileOpen(false)}
                  >
                    <span
                      className="admin-header-profile-item-icon"
                      aria-hidden="true"
                    >
                      🔑
                    </span>
                    Change password
                  </Link>
          
                  <button
                    type="button"
                    className="admin-header-profile-logout"
                    onClick={handleLogout}
                  >
                    <span
                      className="admin-header-profile-logout-icon"
                      aria-hidden="true"
                    >
                      🚪
                    </span>
                    {admin.logoutText}
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>
        <main className="admin-main-content">{children}</main>
      </div>
    </div>
  );
}

export default AdminLayout;
